<script setup lang="ts">
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue'

interface Props {
  items: any[]
  columns?: number
  gap?: number
}

const props = withDefaults(defineProps<Props>(), {
  columns: 2,
  gap: 16,
})

// 存储每个卡片的 DOM 引用和高度
const itemRefs = ref<(HTMLElement | null)[]>([])
const itemHeights = ref<number[]>([])
const observers = ref<Map<number, ResizeObserver>>(new Map())
const isInitialized = ref<Set<number>>(new Set())

// 设置卡片引用并监听高度变化
const setItemRef = (el: HTMLElement | null, index: number) => {
  if (!el) {
    // 清理已移除元素的观察器
    if (observers.value.has(index)) {
      observers.value.get(index)?.disconnect()
      observers.value.delete(index)
      isInitialized.value.delete(index)
    }
    return
  }

  // 如果该索引已经初始化过，跳过
  if (isInitialized.value.has(index) && itemRefs.value[index] === el) {
    return
  }

  itemRefs.value[index] = el

  // 清理旧的观察器
  if (observers.value.has(index)) {
    observers.value.get(index)?.disconnect()
  }

  // 创建 ResizeObserver 监听高度变化
  const observer = new ResizeObserver(() => {
    const height = el.offsetHeight
    if (height > 0 && itemHeights.value[index] !== height) {
      // 使用 nextTick 延迟更新，避免在渲染过程中触发响应式更新
      nextTick(() => {
        itemHeights.value[index] = height
      })
    }
  })

  observer.observe(el)
  observers.value.set(index, observer)
  isInitialized.value.add(index)

  // 初始化高度（使用 nextTick 避免循环）
  nextTick(() => {
    const height = el.offsetHeight
    if (height > 0 && itemHeights.value[index] !== height) {
      itemHeights.value[index] = height
    }
  })
}

// 计算每个卡片的位置（贪心算法：始终放到最短的列）
interface ItemPosition {
  left: string
  top: number
  column: number
}

const itemPositions = computed<ItemPosition[]>(() => {
  if (!props.items.length) return []

  const columnHeights = new Array(props.columns).fill(0)
  const positions: ItemPosition[] = []

  props.items.forEach((_, index) => {
    const height = itemHeights.value[index] || 0

    // 找到当前最短的列
    const minColumn = columnHeights.indexOf(Math.min(...columnHeights))

    // 计算位置（考虑 gap 间距）
    // 第 n 列的 left = (列宽 + gap) * n
    const left = minColumn === 0 ? '0px' : `calc((100% + ${props.gap}px) / ${props.columns} * ${minColumn})`
    const top = columnHeights[minColumn]

    positions.push({ left, top, column: minColumn })

    // 更新列高度（加上卡片高度和间距）
    columnHeights[minColumn] += height + props.gap
  })

  return positions
})

// 计算容器总高度（取最高列的高度）
const containerHeight = computed(() => {
  if (!itemPositions.value.length) return 0

  const columnHeights = new Array(props.columns).fill(0)

  itemPositions.value.forEach((pos, index) => {
    const height = itemHeights.value[index] || 0
    columnHeights[pos.column] = Math.max(columnHeights[pos.column], pos.top + height)
  })

  return Math.max(...columnHeights)
})

// 每列的宽度（考虑 gap 间距）
const columnWidthPercent = computed(() => {
  return `calc((100% - ${props.gap * (props.columns - 1)}px) / ${props.columns})`
})

// 监听 items 变化，重新测量高度
watch(
  () => props.items,
  async () => {
    // 重置初始化状态，允许重新测量
    isInitialized.value.clear()
    await nextTick()
    // 等待 DOM 更新后，ResizeObserver 会自动触发测量
  },
  { deep: true },
)

// 组件卸载时清理所有观察器
onBeforeUnmount(() => {
  observers.value.forEach(observer => observer?.disconnect())
  observers.value.clear()
  isInitialized.value.clear()
})
</script>

<template>
  <div class="relative w-full" :style="{ minHeight: containerHeight + 'px' }">
    <div
      v-for="(item, index) in items"
      :key="item.IdKey || item.id || index"
      :ref="el => setItemRef(el as HTMLElement, index)"
      class="absolute"
      :style="{
        left: itemPositions[index] ? itemPositions[index].left : '0',
        top: itemPositions[index] ? `${itemPositions[index].top}px` : '0',
        width: columnWidthPercent,
        opacity: itemHeights[index] ? 1 : 0,
      }"
    >
      <slot :item="item" :index="index"></slot>
    </div>
  </div>
</template>
